import { useState, useEffect, useMemo } from "react";
import { DataTable, StatusBadge } from "../../components/dashboard";
import { listUsers, updateUser } from "../../lib/api/cloud";
import type { CloudUser, UserStatus } from "../../types/cloud";

type StatusFilter = "all" | UserStatus;

export function UserManagement() {
  const [users, setUsers] = useState<CloudUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [approveUser, setApproveUser] = useState<CloudUser | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchData = async () => {
    try {
      const usersRes = await listUsers();
      setUsers(usersRes.users);
    } catch {
      // Error handling
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredUsers = useMemo(() => {
    let list = filter === "all" ? users : users.filter((u) => u.status === filter);
    // Sort pending users to the top
    list = [...list].sort((a, b) => {
      if (a.status === "pending" && b.status !== "pending") return -1;
      if (a.status !== "pending" && b.status === "pending") return 1;
      return 0;
    });
    return list;
  }, [users, filter]);

  const handleApprove = async () => {
    if (!approveUser) return;
    setActionLoading(true);
    try {
      await updateUser(approveUser.id, { status: "approved" });
      await fetchData();
    } catch {
      // Error handling
    } finally {
      setActionLoading(false);
      setApproveUser(null);
    }
  };

  const handleStatusChange = async (userId: string, status: UserStatus) => {
    setActionLoading(true);
    try {
      await updateUser(userId, { status });
      await fetchData();
    } catch {
      // Error handling
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const columns = [
    {
      key: "email",
      label: "Email",
      sortable: true,
      render: (u: CloudUser) => <span className="font-mono text-sm">{u.email}</span>,
    },
    {
      key: "name",
      label: "Name",
      sortable: true,
      render: (u: CloudUser) => <span className="text-sm">{u.name ?? "—"}</span>,
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (u: CloudUser) => <StatusBadge status={u.status} />,
    },
    {
      key: "role",
      label: "Role",
      sortable: true,
      render: (u: CloudUser) => <StatusBadge status={u.role} />,
    },
    {
      key: "createdAt",
      label: "Registered",
      sortable: true,
      render: (u: CloudUser) => (
        <span className="text-[#999] text-sm">{formatDate(u.createdAt)}</span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      render: (u: CloudUser) => (
        <div className="flex gap-2">
          {u.status === "pending" && (
            <>
              <button
                onClick={() => setApproveUser(u)}
                disabled={actionLoading}
                className="px-2.5 py-1 bg-green-500/10 text-green-400 rounded text-xs font-medium hover:bg-green-500/20 disabled:opacity-40"
              >
                Approve
              </button>
              <button
                onClick={() => handleStatusChange(u.id, "banned")}
                disabled={actionLoading}
                className="px-2.5 py-1 bg-red-500/10 text-red-400 rounded text-xs font-medium hover:bg-red-500/20 disabled:opacity-40"
              >
                Reject
              </button>
            </>
          )}
          {u.status === "approved" && (
            <>
              <button
                onClick={() => handleStatusChange(u.id, "suspended")}
                disabled={actionLoading}
                className="px-2.5 py-1 bg-yellow-500/10 text-yellow-400 rounded text-xs font-medium hover:bg-yellow-500/20 disabled:opacity-40"
              >
                Suspend
              </button>
              <button
                onClick={() => handleStatusChange(u.id, "banned")}
                disabled={actionLoading}
                className="px-2.5 py-1 bg-red-500/10 text-red-400 rounded text-xs font-medium hover:bg-red-500/20 disabled:opacity-40"
              >
                Ban
              </button>
            </>
          )}
          {u.status === "suspended" && (
            <>
              <button
                onClick={() => handleStatusChange(u.id, "approved")}
                disabled={actionLoading}
                className="px-2.5 py-1 bg-green-500/10 text-green-400 rounded text-xs font-medium hover:bg-green-500/20 disabled:opacity-40"
              >
                Unsuspend
              </button>
              <button
                onClick={() => handleStatusChange(u.id, "banned")}
                disabled={actionLoading}
                className="px-2.5 py-1 bg-red-500/10 text-red-400 rounded text-xs font-medium hover:bg-red-500/20 disabled:opacity-40"
              >
                Ban
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  const filterButtons: { label: string; value: StatusFilter }[] = [
    { label: "All", value: "all" },
    { label: "Pending", value: "pending" },
    { label: "Approved", value: "approved" },
    { label: "Suspended", value: "suspended" },
    { label: "Banned", value: "banned" },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#f5f5f5]">User Management</h1>
        <p className="text-[#999] text-sm mt-1">
          {users.length} total users, {users.filter((u) => u.status === "pending").length} pending
        </p>
      </div>

      <div className="bg-[#1a1a1a] border border-[#333] rounded-lg">
        <DataTable
          columns={columns}
          data={filteredUsers}
          keyFn={(u) => u.id}
          emptyMessage="No users found"
          filters={
            <div className="flex gap-2 px-4 pt-4">
              {filterButtons.map((btn) => (
                <button
                  key={btn.value}
                  onClick={() => setFilter(btn.value)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors
                    ${
                      filter === btn.value
                        ? "bg-blue-500/10 text-blue-400"
                        : "text-[#999] hover:text-[#f5f5f5] hover:bg-[#111]"
                    }`}
                >
                  {btn.label}
                </button>
              ))}
            </div>
          }
        />
      </div>

      {/* Approve confirmation dialog */}
      {approveUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-[#1a1a1a] border border-[#333] rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-[#f5f5f5] mb-2">Approve User</h3>
            <p className="text-[#999] text-sm mb-4">
              Approve <span className="text-[#f5f5f5]">{approveUser.email}</span>? The default
              resource plan will be auto-assigned. Users can change their plan later.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setApproveUser(null)}
                className="px-4 py-2 text-sm font-medium text-[#999] hover:text-[#f5f5f5]"
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                disabled={actionLoading}
                className="px-4 py-2 bg-green-500/10 text-green-400 rounded-lg text-sm font-medium hover:bg-green-500/20 disabled:opacity-40"
              >
                {actionLoading ? "Approving..." : "Approve"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
